import { FC, PropsWithChildren, useEffect, useRef } from "react";

import { Box } from "@mui/material";
import Grid from "@mui/material/Grid";

import { FormInfobox } from "@/forms-design-system/FormInfobox";
import { Warning } from "@/icons/outlined";

interface PaymentErrorAlertProps {
  paymentError?: string | null;
}

const PaymentErrorAlert: FC<PropsWithChildren<PaymentErrorAlertProps>> = ({ paymentError }) => {
  const containerRef = useRef<HTMLDivElement>(null);

  // Imperative scroll after React commits the error banner: the user often stays near the submit
  // button while `paymentError` is set asynchronously after `form_submitCommand` (or Stripe steps).
  // A short delay lets the alert’s max-height transition start so `scrollIntoView` targets a stable box.
  useEffect(() => {
    if (!paymentError) {
      return;
    }
    const timeoutId = window.setTimeout(() => {
      containerRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 100);
    return () => window.clearTimeout(timeoutId);
  }, [paymentError]);

  if (!paymentError) {
    return null;
  }

  return (
    <Box
      ref={containerRef}
      sx={(theme) => ({
        maxHeight: paymentError ? 1000 : 0,
        transition: paymentError ? "max-height 0.5s ease-in-out" : "none",
        overflow: "hidden",
        marginBottom: paymentError ? theme.spacing(2) : 0,
        zIndex: 10,
      })}
    >
      <Grid item xs={12}>
        <FormInfobox icon={<Warning />} variant="error" description={paymentError} />
      </Grid>
    </Box>
  );
};

export default PaymentErrorAlert;
